// src/components/SampleRequestModal.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FiX, FiCheck, FiAlertCircle } from "react-icons/fi";

import { createSampleRequest, displayName, explainError } from "../services/db";
import { sendSampleRequestEmail } from "../services/email";

const EMPTY = {
  businessName: "",
  contactName: "",
  phone: "",
  email: "",
  instagram: "",
  city: "",
};

const PHONE_RE = /^(\+?383)?0?4[3-9]\d{6}$/;

const validate = (f) => {
  const e = {};
  if (f.businessName.trim().length < 2)
    e.businessName = "Shkruani emrin e studios ose biznesit.";
  if (f.contactName.trim().length < 3) e.contactName = "Shkruani emrin tuaj.";
  if (!PHONE_RE.test(f.phone.replace(/[\s-]/g, "")))
    e.phone = "Numri duhet të jetë si 044 123 456.";
  if (!/^\S+@\S+\.\S+$/.test(f.email))
    e.email = "Shkruani një email të vlefshëm.";
  if (f.instagram.trim().length < 2)
    e.instagram = "Shkruani profilin tuaj në Instagram.";
  return e;
};

export default function SampleRequestModal({ product, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [failed, setFailed] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.classList.add("no-scroll");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("no-scroll");
    };
  }, [onClose]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setFailed("");

    const found = validate(form);
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }

    setSubmitting(true);
    try {
      const contact = {
        businessName: form.businessName.trim(),
        contactName: form.contactName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        instagram: form.instagram.trim().replace(/^@/, ""),
        city: form.city.trim(),
      };

      const id = await createSampleRequest({
        product,
        contact,
        note: note.trim(),
      });

      // never blocks: the request is already saved
      sendSampleRequestEmail({
        requestId: id,
        productName: displayName(product),
        pmuCode: product.pmuCode,
        contact,
        note: note.trim(),
      });

      setDone(true);
    } catch (err) {
      console.error(err);
      setFailed(explainError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Overlay onMouseDown={onClose}>
      <Sheet
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <Close onClick={onClose} aria-label="Mbyll">
          <FiX />
        </Close>

        {done ? (
          <Success>
            <Mark>
              <FiCheck />
            </Mark>
            <h2>Kërkesa u dërgua</h2>
            <p>
              Faleminderit. Do të shqyrtojmë kërkesën tuaj dhe do t'ju
              kontaktojmë në numrin që latë për të konfirmuar dërgesën e
              mostrës.
            </p>
            <DoneBtn onClick={onClose}>Mbyll</DoneBtn>
          </Success>
        ) : (
          <>
            <Header>
              <Swatch $hex={product.hex}>
                {product.images?.[0] && <img src={product.images[0]} alt="" />}
              </Swatch>
              <div>
                <Kicker>Kërkesë për mostër</Kicker>
                <h2>{displayName(product)}</h2>
                {product.pmuCode && <Code>{product.pmuCode}</Code>}
              </div>
            </Header>

            <Explain>
              Mostrat u dedikohen artistëve dhe studiove profesionale, prandaj
              dërgohen vetëm pas verifikimit. Plotësoni të dhënat e biznesit
              tuaj dhe ju kontaktojmë sa më shpejt.
            </Explain>

            <Form onSubmit={submit} noValidate>
              <Two>
                <Field $err={!!errors.businessName}>
                  <label htmlFor="businessName">
                    Emri i studios / biznesit
                  </label>
                  <input
                    id="businessName"
                    value={form.businessName}
                    onChange={set("businessName")}
                    autoComplete="organization"
                  />
                  {errors.businessName && <Err>{errors.businessName}</Err>}
                </Field>

                <Field $err={!!errors.contactName}>
                  <label htmlFor="contactName">Personi i kontaktit</label>
                  <input
                    id="contactName"
                    value={form.contactName}
                    onChange={set("contactName")}
                    autoComplete="name"
                  />
                  {errors.contactName && <Err>{errors.contactName}</Err>}
                </Field>
              </Two>

              <Two>
                <Field $err={!!errors.phone}>
                  <label htmlFor="samplePhone">Numri i telefonit</label>
                  <input
                    id="samplePhone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="044 123 456"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                  {errors.phone && <Err>{errors.phone}</Err>}
                </Field>

                <Field $err={!!errors.instagram}>
                  <label htmlFor="instagram">Instagram i biznesit</label>
                  <input
                    id="instagram"
                    value={form.instagram}
                    onChange={set("instagram")}
                    placeholder="@studio_juaj"
                  />
                  {errors.instagram && <Err>{errors.instagram}</Err>}
                </Field>
              </Two>

              <Two>
                <Field $err={!!errors.email}>
                  <label htmlFor="sampleEmail">Email</label>
                  <input
                    id="sampleEmail"
                    value={form.email}
                    onChange={set("email")}
                    inputMode="email"
                    autoComplete="email"
                  />
                  {errors.email && <Err>{errors.email}</Err>}
                </Field>

                <Field>
                  <label htmlFor="sampleCity">
                    Qyteti <Optional>(opsionale)</Optional>
                  </label>
                  <input
                    id="sampleCity"
                    value={form.city}
                    onChange={set("city")}
                    autoComplete="address-level2"
                  />
                </Field>
              </Two>

              <Field>
                <label htmlFor="sampleNote">
                  Shënim <Optional>(opsionale)</Optional>
                </label>
                <textarea
                  id="sampleNote"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Çfarë punoni më shpesh, sa klientë keni në javë…"
                />
              </Field>

              {failed && (
                <Failed role="alert">
                  <FiAlertCircle /> {failed}
                </Failed>
              )}

              <Submit type="submit" disabled={submitting}>
                {submitting ? "Duke dërguar…" : "Dërgo kërkesën"}
              </Submit>

              <Small>
                Kërkesa nuk është porosi. Mostra dërgohet vetëm pas konfirmimit
                tonë.
              </Small>
            </Form>
          </>
        )}
      </Sheet>
    </Overlay>
  );
}

/* ------------------------------------------------------------------ */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(12, 10, 9, 0.5);
  backdrop-filter: blur(4px);
  display: grid;
  place-items: center;
  padding: 20px;
  overflow-y: auto;
`;

const Sheet = styled.div`
  position: relative;
  width: 100%;
  max-width: 620px;
  margin: auto;
  padding: 34px;
  border-radius: ${({ theme }) => theme.radius.xl};
  background: ${({ theme }) => theme.color.bg};
  box-shadow: ${({ theme }) => theme.shadow.lg};

  ${({ theme }) => theme.down.sm} {
    padding: 24px 20px;
  }
`;

const Close = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme }) => theme.color.muted};

  &:hover {
    background: ${({ theme }) => theme.color.bgSoft};
    color: ${({ theme }) => theme.color.ink};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
  padding-right: 40px;

  h2 {
    font-size: 1.5rem;
    margin: 2px 0 4px;
  }
`;

const Swatch = styled.div`
  width: 62px;
  height: 62px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ $hex, theme }) => $hex || theme.color.bgSoft};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8%;
  }
`;

const Kicker = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.size.xs};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.brand};
  font-weight: ${({ theme }) => theme.weight.semi};
`;

const Code = styled.span`
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};
`;

const Explain = styled.p`
  margin: 0 0 24px;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.bgSoft};
  font-size: ${({ theme }) => theme.size.sm};
  color: ${({ theme }) => theme.color.body};
`;

const Form = styled.form``;

const Two = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 16px;

  ${({ theme }) => theme.down.sm} {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    margin-bottom: 7px;
    font-size: ${({ theme }) => theme.size.xs};
    font-weight: ${({ theme }) => theme.weight.semi};
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.color.muted};
  }

  input,
  textarea {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid
      ${({ $err, theme }) => ($err ? theme.color.danger : theme.color.line)};
    border-radius: ${({ theme }) => theme.radius.md};
    font-size: ${({ theme }) => theme.size.sm};
    resize: vertical;

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.color.brand};
      box-shadow: ${({ theme }) => theme.shadow.focus};
    }
  }
`;

const Optional = styled.span`
  text-transform: none;
  letter-spacing: 0;
  font-weight: ${({ theme }) => theme.weight.regular};
`;

const Err = styled.p`
  margin: 6px 0 0;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.danger};
`;

const Failed = styled.p`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 16px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.dangerSoft};
  color: ${({ theme }) => theme.color.danger};
  font-size: ${({ theme }) => theme.size.sm};
`;

const Submit = styled.button`
  width: 100%;
  min-height: 52px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.ink};
  color: #fff;
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  transition: background 0.3s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.color.brand};
  }
  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const Small = styled.p`
  margin: 14px 0 0;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};
  text-align: center;
`;

const Success = styled.div`
  text-align: center;
  padding: 20px 0 8px;

  h2 {
    font-size: 1.6rem;
    margin-bottom: 12px;
  }

  p {
    max-width: 44ch;
    margin: 0 auto;
    color: ${({ theme }) => theme.color.body};
  }
`;

const Mark = styled.div`
  width: 66px;
  height: 66px;
  margin: 0 auto 22px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.okSoft};
  color: ${({ theme }) => theme.color.ok};
  font-size: 1.7rem;
`;

const DoneBtn = styled.button`
  margin-top: 26px;
  min-height: 48px;
  padding: 0 32px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.ink};
  color: #fff;
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};

  &:hover {
    background: ${({ theme }) => theme.color.brand};
  }
`;
